import { useEffect, useMemo, useState } from "react";
import { MapPin, ArrowUpRight, CalendarClock, Route, Clock, Flag, ExternalLink, Tag, Bike, CheckCircle2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import { Reveal } from "../components/Reveal";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { dayMonth, fmtDate, TEAM_LABELS } from "../lib/format";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TEAM_FILTERS = [
  { value: "", label: "Toutes les équipes" },
  { value: "feminine", label: "Féminine N1/UCI" },
  { value: "masculine", label: "Homme N3" },
  { value: "jeunes", label: "École de Vélo" },
  { value: "loisir", label: "Loisir et Cyclo" },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

function EventCard({ event, past, onOpen, index }) {
  const d = dayMonth(event.date);
  return (
    <Reveal delay={Math.min(index, 5) * 0.05}>
      <button
        type="button"
        onClick={() => onOpen(event)}
        data-testid={`event-card-${event.id}`}
        className={`group w-full text-left bg-white border transition-[border-color,box-shadow,opacity] duration-300 flex flex-col h-full ${
          past
            ? "border-navy/10 opacity-60 hover:opacity-100"
            : "border-navy/10 hover:border-rouge hover:shadow-[0_12px_40px_-12px_rgba(10,17,40,0.25)]"
        }`}
      >
        <div className="flex items-stretch">
          <div className={`shrink-0 w-[76px] flex flex-col items-center justify-center py-5 text-white transition-colors duration-300 ${past ? "bg-steel" : "bg-navy group-hover:bg-rouge"}`}>
            <span className="font-heading font-semibold text-3xl leading-none">{d.day}</span>
            <span className="text-[10px] uppercase tracking-widest mt-1.5 opacity-80">{d.month}</span>
            <span className="text-[10px] uppercase tracking-widest opacity-60">{d.year}</span>
          </div>
          <div className="min-w-0 flex-1 p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-heading uppercase font-semibold tracking-tight text-navy text-lg leading-snug group-hover:text-rouge transition-colors duration-300">
                {event.name}
              </h3>
              <ArrowUpRight size={18} className="shrink-0 mt-1 text-navy/30 group-hover:text-rouge transition-[color,transform] duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-sm text-steel">
              <MapPin size={13} className="text-rouge shrink-0" />
              <span className="truncate">{event.location || "Lieu à confirmer"}{event.comarque ? ` · ${event.comarque}` : ""}</span>
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="uppercase tracking-wider text-[11px] font-semibold text-navy/60 border border-navy/15 px-2.5 py-1">
                {event.categories || event.category}
              </span>
              {past ? (
                <span className="uppercase tracking-wider text-[11px] font-bold text-steel bg-steel/10 px-2.5 py-1">Terminée</span>
              ) : (
                event.status && <span className="uppercase tracking-wider text-[11px] font-bold text-rouge bg-rouge/10 px-2.5 py-1">{event.status}</span>
              )}
            </div>
          </div>
        </div>
      </button>
    </Reveal>
  );
}

function DetailRow({ icon: Icon, label, value, testId }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-4 py-3.5 border-b border-navy/10 last:border-0">
      <div className="shrink-0 w-9 h-9 bg-mist flex items-center justify-center text-rouge">
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <p className="font-heading uppercase tracking-wider text-[11px] text-steel">{label}</p>
        <p className="text-navy font-medium mt-0.5" data-testid={testId}>{value}</p>
      </div>
    </div>
  );
}

const PARTICIPATION_BADGES = {
  "DEMANDE": { label: "Demande envoyée au club", cls: "bg-amber-100 text-amber-700" },
  "VALIDÉE": { label: "Participation validée par le club", cls: "bg-navy text-white" },
  "CONFIRMÉE": { label: "Inscription officiellement confirmée", cls: "bg-green-100 text-green-700" },
};

function ParticipationBlock({ event }) {
  const { user } = useAuth();
  const [participation, setParticipation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role === "licencie") {
      setLoading(true);
      api.get("/espace/participations")
        .then((r) => setParticipation(r.data.find((p) => p.event_id === event.id) || null))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [user, event.id]);

  const participer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/espace/courses/${event.id}/participer`);
      setParticipation(data);
      toast.success("Votre demande de participation a été envoyée au club");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const badge = participation ? PARTICIPATION_BADGES[participation.statut] : null;

  return (
    <div className="mt-8 border-t border-navy/10 pt-8" data-testid="event-participation-block">
      {user?.role === "licencie" ? (
        <>
          <p className="font-heading uppercase tracking-wider text-[11px] text-steel mb-4">Votre participation</p>
          {loading ? null : badge ? (
            <div className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold ${badge.cls}`} data-testid="event-participation-status">
              <CheckCircle2 size={16} /> {badge.label}
            </div>
          ) : (
            <button
              type="button"
              onClick={participer}
              disabled={busy}
              data-testid="event-participate-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Bike size={16} />}
              M'inscrire à cette course
            </button>
          )}
          {event.registration_url && (
            <a
              href={event.registration_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="event-registration-external"
              className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2.5 border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-4 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
            >
              S'inscrire sur le site de la course
              <ExternalLink size={14} />
            </a>
          )}
          <p className="mt-4 text-xs text-steel leading-relaxed max-w-lg" data-testid="event-participation-note">
            Vous pouvez demander votre participation au club afin que nous gérions votre inscription, ou vous inscrire
            directement auprès de l'organisateur lorsque cette possibilité est proposée.
          </p>
        </>
      ) : (
        event.registration_url && (
          <a
            href={event.registration_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="event-registration-external"
            className="inline-flex items-center justify-center gap-2.5 border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-4 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
          >
            S'inscrire sur le site de la course
            <ExternalLink size={14} />
          </a>
        )
      )}
    </div>
  );
}

function EventDetail({ event, onClose }) {
  const open = Boolean(event);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-0 bg-white border-navy/10 sm:rounded-none"
        data-testid="event-detail-dialog"
        aria-describedby={undefined}
      >
        {event && (
          <div>
            {event.photo && (
              <div className="aspect-[16/9] bg-navy overflow-hidden">
                <img
                  src={`${API_BASE}/api/files/${event.photo}`}
                  alt={event.name}
                  className="w-full h-full object-cover"
                  data-testid="event-detail-photo"
                />
              </div>
            )}
            <div className="p-6 sm:p-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="bg-navy text-white font-heading uppercase tracking-wider text-xs px-3 py-1.5" data-testid="event-detail-date">
                  {fmtDate(event.date)}
                </span>
                {event.categories && (
                  <span className="bg-rouge/10 text-rouge font-heading uppercase tracking-wider text-xs px-3 py-1.5" data-testid="event-detail-categories">
                    {event.categories}
                  </span>
                )}
              </div>
              <DialogTitle className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-4xl leading-tight" data-testid="event-detail-name">
                {event.name}
              </DialogTitle>

              <div className="mt-8 grid sm:grid-cols-2 gap-x-8">
                <DetailRow icon={MapPin} label="Lieu" value={event.location} testId="event-detail-location" />
                <DetailRow icon={MapPin} label="Comarque / Région" value={event.comarque} testId="event-detail-comarque" />
                <DetailRow icon={Tag} label="Type de course" value={event.race_type} testId="event-detail-type" />
                <DetailRow icon={Flag} label="Adresse de départ" value={event.start_address} testId="event-detail-address" />
                <DetailRow icon={Clock} label="Contrôle des licences" value={event.license_check_time} testId="event-detail-license-time" />
                <DetailRow icon={CalendarClock} label="Heure de départ" value={event.start_time} testId="event-detail-start-time" />
                <DetailRow icon={Route} label="Distance" value={event.distance_km} testId="event-detail-distance" />
                <DetailRow icon={Route} label="Distance du circuit" value={event.circuit_km} testId="event-detail-circuit" />
                <DetailRow icon={CalendarClock} label="Date limite d'inscription" value={event.registration_deadline ? fmtDate(event.registration_deadline) : ""} testId="event-detail-deadline" />
              </div>

              {event.registration_info && (
                <div className="mt-6 bg-mist border-l-2 border-navy p-5" data-testid="event-detail-reginfo">
                  <p className="font-heading uppercase tracking-wider text-[11px] text-steel mb-2">Informations pour l'inscription</p>
                  <p className="text-navy/85 text-sm leading-relaxed whitespace-pre-line">{event.registration_info}</p>
                </div>
              )}

              <ParticipationBlock event={event} />

              {event.info && (
                <div className="mt-6 bg-mist border-l-2 border-rouge p-5" data-testid="event-detail-info">
                  <p className="font-heading uppercase tracking-wider text-[11px] text-steel mb-2">Informations complémentaires</p>
                  <p className="text-navy/85 text-sm leading-relaxed whitespace-pre-line">{event.info}</p>
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                {event.official_url && (
                  <a
                    href={event.official_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="event-detail-official-link"
                    className="inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-navy transition-colors duration-300"
                  >
                    Voir le site officiel
                    <ExternalLink size={15} />
                  </a>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="event-detail-close"
                  className="inline-flex items-center justify-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-4 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Calendrier() {
  const [events, setEvents] = useState(null);
  const [team, setTeam] = useState("");
  const [month, setMonth] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/meta/categories").then((r) => setCategories(r.data.event_categories)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (team) params.set("team", team);
    if (month) params.set("month", month);
    if (category) params.set("category", category);
    api.get(`/events?${params.toString()}`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [team, month, category]);

  const { upcoming, past } = useMemo(() => {
    const t = todayStr();
    const all = events || [];
    return {
      upcoming: all.filter((e) => e.date >= t),
      past: all.filter((e) => e.date < t).slice().reverse(),
    };
  }, [events]);

  return (
    <>
      <SEO title="Calendrier" description="Calendrier des compétitions et événements du Club Cycliste Le Boulou : courses, entraînements et événements du club." />
      <PageHeader kicker="Saison" title="Calendrier" />

      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 mb-12" data-testid="calendar-filters">
              {TEAM_FILTERS.map((t) => (
                <button
                  key={t.value}
                  data-testid={`calendar-filter-team-${t.value || "all"}`}
                  onClick={() => setTeam(t.value)}
                  className={`font-heading uppercase tracking-wider text-xs px-4 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
                    team === t.value ? "bg-navy border-navy text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"
                  }`}
                >
                  {t.label}
                </button>
              ))}
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="calendar-filter-month"
                className="border border-navy/20 bg-white px-4 py-2.5 text-sm text-navy focus:outline-none focus:border-rouge"
              />
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="calendar-filter-category"
                className="border border-navy/20 bg-white px-4 py-2.5 text-sm text-navy focus:outline-none focus:border-rouge"
              >
                <option value="">Toutes catégories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </Reveal>

          {events === null ? null : events.length === 0 ? (
            <EmptyState
              title="Aucun événement pour le moment"
              message="Le calendrier des compétitions et événements du club sera publié ici. Les licenciés pourront bientôt s'inscrire directement depuis cette page."
              testId="calendar-empty"
            />
          ) : (
            <>
              {upcoming.length > 0 && (
                <div data-testid="calendar-upcoming">
                  <Reveal>
                    <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-3xl mb-8 flex items-center gap-4">
                      <span className="w-8 h-[3px] bg-rouge inline-block" />
                      Prochaines courses
                    </h2>
                  </Reveal>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 mb-16">
                    {upcoming.map((e, i) => (
                      <EventCard key={e.id} event={e} past={false} onOpen={setSelected} index={i} />
                    ))}
                  </div>
                </div>
              )}

              {past.length > 0 && (
                <div data-testid="calendar-past">
                  <Reveal>
                    <h2 className="font-heading uppercase font-semibold tracking-tight text-navy/50 text-2xl sm:text-3xl mb-8 flex items-center gap-4">
                      <span className="w-8 h-[3px] bg-navy/25 inline-block" />
                      Courses passées
                    </h2>
                  </Reveal>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {past.map((e, i) => (
                      <EventCard key={e.id} event={e} past onOpen={setSelected} index={i} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <EventDetail event={selected} onClose={() => setSelected(null)} />
    </>
  );
}
